import { Link } from '@tanstack/react-router'
import { odooUrl } from '@/lib/utils/odoo-url'
import type { ActivityEntry } from '../api/types'

function relativeTime(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const deltaSec = Math.floor((now.getTime() - t) / 1000)
  if (deltaSec < 30) return 'just now'
  if (deltaSec < 60) return `${deltaSec}s ago`
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`
  if (deltaSec < 7 * 86400) return `${Math.floor(deltaSec / 86400)}d ago`
  return new Date(t).toLocaleDateString()
}

function prettyAction(action: string): string {
  return action.replace(/_/g, ' ')
}

export function ActivityFeed({ entries }: { entries: ActivityEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="flex min-h-[160px] items-center justify-center rounded-sm border border-dashed border-border-default bg-surface-sunk p-4 text-center">
        <span className="text-sm text-tertiary">
          No recent activity. CARLA-executed actions show up here.
        </span>
      </div>
    )
  }
  return (
    <ul className="flex flex-col divide-y divide-border-subtle rounded-sm border border-border-subtle bg-surface">
      {entries.map((e) => (
        <li key={e.id} className="flex flex-col gap-0.5 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-tertiary">{relativeTime(e.created_at)}</span>
            <span className="text-tertiary">·</span>
            <span className="text-secondary">{e.user || 'system'}</span>
            <span className="text-tertiary">·</span>
            <span className="font-medium text-primary">{prettyAction(e.action)}</span>
            <a
              href={odooUrl(e.odoo_model, e.record_id)}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-xs text-link hover:underline"
            >
              #{e.record_id}
            </a>
          </div>
          <span className="truncate text-xs text-secondary">{e.summary}</span>
        </li>
      ))}
      <li className="flex justify-end border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-xs">
        <Link to="/actions" className="text-link hover:underline">
          See full Action History →
        </Link>
      </li>
    </ul>
  )
}
